import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WatchLaterIcon from '@mui/icons-material/WatchLater';
import { useCallback, useEffect, useState } from 'react';
import LinkedUpStatusDetails from '../components/LinkedUpStatusDetails';

const formatLinkedUpCreatedDate = (rawDate) => {
  if (!rawDate) {
    return '-';
  }

  const normalizedDate = String(rawDate).replace(/\.(\d{3})\d*(?=[+-]\d{2}:?\d{2}|Z$)/, '.$1');
  const parsedDate = new Date(normalizedDate);

  if (Number.isNaN(parsedDate.getTime())) {
    return String(rawDate);
  }

  const formatter = new Intl.DateTimeFormat('es-MX', {
    day: '2-digit',
    month: 'short',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });

  const parts = formatter.formatToParts(parsedDate);
  const getPart = (type) => parts.find((part) => part.type === type)?.value || '';

  return `${getPart('day')} ${getPart('month')} ${getPart('year')}, ${getPart('hour')}:${getPart('minute')}`;
};

const EMPTY_ARRAY = [];

export const useIncodeFlow = ({
  createIncodeLinks,
  createIncodeLinksAction,
  cleanCreateIncodeLinks,
  getIncodeStatus,
  getIncodeStatusAction,
  cleanIncodeStatus,
  legalId,
  orderId,
  uuidFromPath,
  navigateToRfcSearch,
  setValue,
  dialog,
  downloadPdf,
  downloadPdfAction,
  cleanDownloadPdf,
  consultLegalId,
  consultLegalIdAction,
  cleanConsultLegalId,
}) => {
  const [isIncodeDialogOpen, setIsIncodeDialogOpen] = useState(false);
  const [incodeLinksDialog, setIncodeLinksDialog] = useState();
  const [incodeLinksLoading, setIncodeLinksLoading] = useState(false);
  const [shouldBlockPageReload, setShouldBlockPageReload] = useState(false);

  const [isDownloadPdf, setIsDownloadPdf] = useState(false);
  const [downloadPdfError, setDownloadPdfError] = useState(false);
  const [successDialogData, setSuccessDialogData] = useState(null);

  const {
    setIsDialogOpen,
    setIsIncodeStatusDialogLocked,
    setIcon,
    setDialogTitleMessage,
    setDialogMessage,
    setAditionalInfoDialog,
  } = dialog;

  const incodeStatusCode = getIncodeStatus?.status;
  const incodeStatusResponseStatus = getIncodeStatus?.payload?.response?.status;
  const incodeStatusId = getIncodeStatus?.payload?.linked_up_status_id;
  const incodeStatusName = getIncodeStatus?.payload?.status_name;
  const incodeStatusCreated = getIncodeStatus?.payload?.created;
  const incodeStatusOrderId = getIncodeStatus?.payload?.linked_up_order_id;
  const incodeStatusErrorTitle = getIncodeStatus?.payload?.title;
  const incodeStatusErrorDetail = getIncodeStatus?.payload?.error_details || EMPTY_ARRAY;

  const getIncodeStatusExecute = useCallback(
    (interview_id) => {
      const interviewIdToSend = interview_id || uuidFromPath;

      if (!interviewIdToSend) {
        return;
      }

      getIncodeStatusAction({
        interview_id: interviewIdToSend,
      });
    },
    [getIncodeStatusAction, uuidFromPath],
  );

  const generateIncodeLinks = useCallback(() => {
    setIsIncodeDialogOpen(true);
    setIncodeLinksLoading(true);
    createIncodeLinksAction({
      legal_id: legalId,
      order_id: orderId,
    });
  }, [createIncodeLinksAction, legalId, orderId]);

  const downloadPdfExecute = useCallback(() => {
    setDownloadPdfError(false);
    setIsDownloadPdf(true);
    if (!legalId || !orderId) {
      consultLegalIdAction({
        incode_id: uuidFromPath,
      });
      return;
    }
    downloadPdfAction({
      legal_id: legalId,
      order_id: orderId,
    });
  }, [downloadPdfAction, consultLegalIdAction, legalId, orderId, uuidFromPath]);

  const handleCloseIncodeDialog = useCallback(() => {
    setIsIncodeDialogOpen(false);
    setSuccessDialogData(null);
    cleanCreateIncodeLinks();
    cleanIncodeStatus();
  }, [cleanCreateIncodeLinks, cleanIncodeStatus]);

  useEffect(() => {
    if (!successDialogData) {
      return;
    }

    setAditionalInfoDialog(
      <LinkedUpStatusDetails
        type='success'
        folio={successDialogData.folio}
        date={successDialogData.date}
        onDownload={downloadPdfExecute}
        isDownloadPdf={isDownloadPdf}
        downloadPdfError={downloadPdfError}
      />,
    );
  }, [
    downloadPdfError,
    downloadPdfExecute,
    isDownloadPdf,
    setAditionalInfoDialog,
    successDialogData,
  ]);

  useEffect(() => {
    if (!uuidFromPath) {
      return;
    }

    getIncodeStatusAction({
      interview_id: uuidFromPath,
    });
  }, [uuidFromPath, getIncodeStatusAction]);

  useEffect(() => {
    if (createIncodeLinks?.status === 200) {
      setIncodeLinksLoading(false);
      setIsIncodeStatusDialogLocked(false);
      setIncodeLinksDialog(createIncodeLinks?.payload);
    } else if (createIncodeLinks?.payload?.response?.status > 200) {
      setIsIncodeDialogOpen(false);
      setIsIncodeStatusDialogLocked(false);
      setIcon(<CancelIcon sx={{ fontSize: 40, color: '#D32F2F' }} />);
      setDialogTitleMessage(createIncodeLinks?.payload?.response?.data?.title);
      setDialogMessage(createIncodeLinks?.payload?.response?.data?.detail ?? 'Ocurrió un error.');
      setIsDialogOpen(true);
      setIncodeLinksLoading(false);
    }

    setValue('direct_number', '');
  }, [
    createIncodeLinks,
    setIncodeLinksLoading,
    setIsIncodeStatusDialogLocked,
    setIncodeLinksDialog,
    setIsIncodeDialogOpen,
    setIcon,
    setDialogTitleMessage,
    setDialogMessage,
    setIsDialogOpen,
    setValue,
  ]);

  useEffect(() => {
    if (!shouldBlockPageReload) {
      return undefined;
    }

    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = '';
      return '';
    };

    const handleKeyDown = (event) => {
      const isRefreshKey =
        event.key === 'F5' ||
        ((event.ctrlKey || event.metaKey) && (event.key === 'r' || event.key === 'R'));

      if (!isRefreshKey) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [shouldBlockPageReload]);

  useEffect(() => {
    if (incodeStatusCode === 200) {
      setShouldBlockPageReload(true);
      setIsIncodeDialogOpen(false);
      setIsIncodeStatusDialogLocked(true);

      setDialogTitleMessage(incodeStatusName);

      switch (incodeStatusId) {
        case 1: {
          const interviewId = incodeLinksDialog?.uuid || uuidFromPath;

          setIcon(<WatchLaterIcon sx={{ fontSize: 40, color: '#f7a14b' }} />);
          setDialogMessage('La vinculación esta pendiente.');
          setAditionalInfoDialog(
            <LinkedUpStatusDetails
              type='pending'
              onPoll={() => {
                if (!interviewId) {
                  console.warn('No se encontró interview_id para consultar nuevamente.');
                  return;
                }

                getIncodeStatusExecute(interviewId);
              }}
            />,
          );
          setIsDialogOpen(true);
          break;
        }

        case 3:
        case 4: {
          const linkedUpOrderId = incodeStatusOrderId || '-';
          const createdAtFormatted = formatLinkedUpCreatedDate(incodeStatusCreated);

          setSuccessDialogData({
            folio: linkedUpOrderId,
            date: createdAtFormatted,
          });
          setDownloadPdfError(false);

          setDialogMessage(
            'La vinculación entre la persona moral y la(s) linea(s) añadidas se realizó con éxito.',
          );
          setIcon(<CheckCircleIcon sx={{ fontSize: 40, color: '#724ecb' }} />);
          setIsDialogOpen(true);
          setAditionalInfoDialog(
            <LinkedUpStatusDetails
              type='success'
              folio={linkedUpOrderId}
              date={createdAtFormatted}
              onDownload={downloadPdfExecute}
              isDownloadPdf={isDownloadPdf}
              downloadPdfError={downloadPdfError}
            />,
          );
          break;
        }

        case 5: {
          const errorDetails = incodeStatusErrorDetail || [];
          const hasErrorDetails = Array.isArray(errorDetails) && errorDetails.length > 0;

          setIcon(<CancelIcon sx={{ fontSize: 40, color: '#D32F2F' }} />);
          setIsDialogOpen(true);

          if (hasErrorDetails) {
            setDialogMessage('No fue posible procesar la vinculación debido al siguiente motivo');
            setAditionalInfoDialog(
              <LinkedUpStatusDetails
                type='error-details'
                errorDetails={errorDetails}
                onAccept={navigateToRfcSearch}
              />,
            );
          } else {
            setDialogMessage('No pudimos procesar la vinculación, intenta nuevamente.');
            setAditionalInfoDialog(
              <LinkedUpStatusDetails
                type='retry'
                onRetry={() => {
                  setIsDialogOpen(false);
                  generateIncodeLinks();
                }}
              />,
            );
          }

          break;
        }

        case 6: {
          const interviewId = incodeLinksDialog?.uuid || uuidFromPath;

          setIcon(<WatchLaterIcon sx={{ fontSize: 40, color: '#f7a14b' }} />);
          setDialogMessage('Espera un momento y vuelve a intentarlo.');
          setAditionalInfoDialog(
            <LinkedUpStatusDetails
              type='pending'
              onPoll={() => {
                if (!interviewId) {
                  console.warn('No se encontró interview_id para consultar nuevamente.');
                  return;
                }
                getIncodeStatusExecute(interviewId);
              }}
            />,
          );
          setIsDialogOpen(true);
          break;
        }

        case 8: {
          const interviewId = incodeLinksDialog?.uuid || uuidFromPath;

          setIcon(<WatchLaterIcon sx={{ fontSize: 40, color: '#f7a14b' }} />);
          setDialogMessage('La vinculación esta pendiente de validación manual.');
          setAditionalInfoDialog(
            <LinkedUpStatusDetails
              type='pending'
              onPoll={() => {
                if (!interviewId) {
                  console.warn('No se encontró interview_id para consultar nuevamente.');
                  return;
                }

                getIncodeStatusExecute(interviewId);
              }}
            />,
          );
          setIsDialogOpen(true);
          break;
        }

        default: {
          setSuccessDialogData(null);
          setIcon(<CancelIcon sx={{ fontSize: 40, color: '#D32F2F' }} />);
          setDialogMessage('Error al procesar la vinculación.');
          setAditionalInfoDialog(null);

          setAditionalInfoDialog(
            <LinkedUpStatusDetails type='error' onAccept={navigateToRfcSearch} />,
          );
          setIsDialogOpen(true);
          break;
        }
      }

      cleanIncodeStatus();
    } else if (incodeStatusResponseStatus > 200) {
      setSuccessDialogData(null);
      setIsIncodeDialogOpen(false);
      setIsIncodeStatusDialogLocked(true);
      setAditionalInfoDialog(null);
      setIcon(<CancelIcon sx={{ fontSize: 40, color: '#D32F2F' }} />);
      setDialogTitleMessage(incodeStatusErrorTitle);
      setDialogMessage(incodeStatusErrorDetail[0] || 'No pudimos procesar la vinculación.');
      setAditionalInfoDialog(<LinkedUpStatusDetails type='error' onAccept={navigateToRfcSearch} />);
      setIsDialogOpen(true);
      cleanCreateIncodeLinks();
      cleanIncodeStatus();
    }
  }, [
    cleanCreateIncodeLinks,
    cleanIncodeStatus,
    downloadPdfExecute,
    getIncodeStatusExecute,
    generateIncodeLinks,
    incodeLinksDialog,
    incodeStatusCode,
    incodeStatusCreated,
    incodeStatusErrorDetail,
    incodeStatusErrorTitle,
    incodeStatusId,
    incodeStatusName,
    incodeStatusOrderId,
    incodeStatusResponseStatus,
    navigateToRfcSearch,
    setAditionalInfoDialog,
    setDialogMessage,
    setDialogTitleMessage,
    setIcon,
    setIsDialogOpen,
    setIsIncodeStatusDialogLocked,
    setSuccessDialogData,
    downloadPdfError,
    isDownloadPdf,
    uuidFromPath,
  ]);

  useEffect(() => {
    if (consultLegalId?.status === 200) {
      setIsDownloadPdf(false);
      window.open(consultLegalId?.payload?.url?.url, '_blank');
      cleanConsultLegalId();
      navigateToRfcSearch();
    } else if (consultLegalId?.payload?.response?.status > 200) {
      setIsDownloadPdf(false);
      setDownloadPdfError(true);
      cleanConsultLegalId();
    }

    setValue('direct_number', '');
  }, [cleanConsultLegalId, consultLegalId, setValue, navigateToRfcSearch]);

  useEffect(() => {
    if (downloadPdf?.status === 200) {
      setIsDownloadPdf(false);
      window.open(downloadPdf?.payload?.url?.url, '_blank');
      cleanDownloadPdf();
      navigateToRfcSearch();
    } else if (downloadPdf?.payload?.response?.status > 200) {
      setIsDownloadPdf(false);
      setDownloadPdfError(true);
      cleanDownloadPdf();
    }

    setValue('direct_number', '');
  }, [cleanDownloadPdf, downloadPdf, setValue, navigateToRfcSearch]);

  return {
    isIncodeDialogOpen,
    incodeLinksDialog,
    handleCloseIncodeDialog,
    incodeLinksLoading,
    generateIncodeLinks,
    getIncodeStatusExecute,
  };
};
